import { Group, Stack } from "@mantine/core"
import { PaginationState, RowSelectionState } from "@tanstack/react-table"
import { DEFAULT_PAGE_SIZE, InfoBox, Loader, ColumnManager } from "@tfg/ui"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import z from "zod"

import { InlineBanner } from "~/components/InlineBanner"
import { ProductFilters } from "~/components/ProductFilters"
import { ProductSearch } from "~/components/ProductSearch"
import { useSearchParamsFilters } from "~/components/hooks/useSearchParamsFilters"
import { CustomTable } from "~/components/table/CustomTable"
import {
  FindProductsCustomFieldsRequestFiltersDto,
  FindProductsCustomFieldsRequestSortsDto,
  useFindProductsCustomFields,
} from "~/gen/api"
import { useGetColumnManagerTranslations } from "~/hooks/table"
import { useGetOrganizationSettingsQuery } from "~/pages/Brand/CommunicationStudio/OrganizationGeneralSettings/hooks/useGetOrganizationSettingsQuery"
import { ProductCustomFieldsSorts } from "~/pages/Brand/Products/List/Components/ProductsTables/custom-fields/ProductCustomFieldsSorts"
import { useProductsCustomFieldsTable } from "~/pages/Brand/Products/List/Components/ProductsTables/custom-fields/useCustomFieldsTable"
import { useCustomFieldsHeaders } from "~/pages/Brand/Products/List/Components/headers.tsx"
import { ProductsListView } from "~/pages/Brand/Products/List/constants.ts"
import { useAuth } from "~/store/auth-context.tsx"

export const CustomFieldsProductsTable = () => {
  const view = ProductsListView.CUSTOM_FIELDS

  const { t } = useTranslation()
  const columnManagerTranslations = useGetColumnManagerTranslations()
  const { user, isAdmin } = useAuth()

  const [filtersValue, setFiltersValue] = useSearchParamsFilters(
    z.custom<FindProductsCustomFieldsRequestFiltersDto>()
  )

  const { data: org } = useGetOrganizationSettingsQuery(user?.organization?.id)

  const [pageCount, setPageCount] = useState<number>(0)
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  })

  const pagination = useMemo(
    () => ({
      pageIndex,
      pageSize,
    }),
    [pageIndex, pageSize]
  )

  const fetchDataOptions = {
    pageIndex,
    pageSize,
  }

  const [sorts, setSorts] = useState<FindProductsCustomFieldsRequestSortsDto>(
    {}
  )

  const {
    data: productsData,
    isPending: isLoadingProducts,
    isFetching: isFetchingProducts,
  } = useFindProductsCustomFields({
    filters: {
      ...filtersValue,
      lang: user?.locale,
    },
    pagination: {
      pageIndex: fetchDataOptions.pageIndex + 1,
      pageSize: fetchDataOptions.pageSize,
    },
    sorts,
  })

  useEffect(() => {
    if (productsData?.data.totalProducts === 0) {
      setPageCount(0)
    }
    if (productsData?.data.totalProducts) {
      setPageCount(Math.ceil(productsData?.data.totalProducts / pageSize))
    }
  }, [productsData?.data.totalProducts, pageSize])

  const products = useMemo(() => {
    return productsData?.data.products ?? []
  }, [productsData])

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [isColumnManagerOpen, setIsColumnManagerOpen] = useState(false)
  const customHeaders = useCustomFieldsHeaders(products)

  const table = useProductsCustomFieldsTable({
    pagination,
    rowSelection,
    setPagination,
    setRowSelection,
    pageCount,
    products,
    customFieldKeys: Object.keys(customHeaders),
  })

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }))
  }, [filtersValue])

  let adminMessage: string | null = null

  if (org?.settings?.customFieldsPlan !== "EXPERT") {
    if (isAdmin) {
      adminMessage =
        "You are seeing the content of this page because you are an admin. Non-admin user see a message explaining that they need to upgrade their plan to see this content. Go to the organization settings page to change the plan."
    } else {
      return (
        <InfoBox
          title={t("simulation.notSubscribed.title")}
          message={t("simulation.notSubscribed.message")}
          variant="info"
        />
      )
    }
  }

  return (
    <Stack>
      {adminMessage && (
        <InlineBanner
          type="admin"
          size="xs"
          message={t("simulation.notSubscribed.title")}
        >
          {adminMessage}
        </InlineBanner>
      )}
      <Group>
        <ProductSearch
          filtersValue={filtersValue}
          setFiltersValue={setFiltersValue}
          isFetching={isFetchingProducts}
        />
        <ProductCustomFieldsSorts sorts={sorts} setSorts={setSorts} />
        <ProductFilters<FindProductsCustomFieldsRequestFiltersDto>
          setFiltersValue={setFiltersValue}
          filtersValue={filtersValue}
        />
        <ColumnManager
          table={table}
          translations={columnManagerTranslations}
          isOpen={isColumnManagerOpen}
          setIsOpen={setIsColumnManagerOpen}
        />
      </Group>
      {isLoadingProducts ? (
        <Loader fullPage />
      ) : (
        <CustomTable
          table={table}
          view={view}
          manualPaginationCount={productsData?.data.totalProducts || 0}
          stickyHeader
          layout="fixed"
          setOpenColumnManager={setIsColumnManagerOpen}
        />
      )}
    </Stack>
  )
}
